// Top-level kiosk app. The outer component wires up the API/bridge clients
// and the config + language providers; KioskApp owns the state machine via
// useReducer and runs the side effects (HTTP calls, bridge WS subscription,
// print sequencing) that fire on each phase transition. Screens are pure
// presentational components that receive state + dispatch.

import type {
  CreateOrderRequest,
  PrintableTicket,
} from "@entradart/kiosk-shared/api";
import type { PrintJob } from "@entradart/kiosk-shared/bridge";
import {
  type MutableRefObject,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { ApiClient } from "./clients/api-client";
import { BridgeClient } from "./clients/bridge-client";
import { EventLog } from "./clients/event-log";
import {
  KioskConfigProvider,
  useKioskConfig,
} from "./config/KioskConfigContext";
import {
  resolveRuntimeConfig,
  type ViteEnvLike,
} from "./config/runtime-config";
import {
  runConfirmOrder,
  runSaleOnBridge,
  type SaleFrames,
} from "./effects/sale-effects";
import { LangProvider, useLang } from "./i18n/LangContext";
import { AttractScreen } from "./screens/AttractScreen";
import { AwaitCardScreen } from "./screens/AwaitCardScreen";
import { CardProcessingScreen } from "./screens/CardProcessingScreen";
import { DateScreen } from "./screens/DateScreen";
import { DoneScreen } from "./screens/DoneScreen";
import { LoadingScreen } from "./screens/LoadingScreen";
import { OutOfServiceScreen } from "./screens/OutOfServiceScreen";
import { PaymentFailedScreen } from "./screens/PaymentFailedScreen";
import { PrintErrorScreen } from "./screens/PrintErrorScreen";
import { PrintingScreen } from "./screens/PrintingScreen";
import { ReviewScreen } from "./screens/ReviewScreen";
import { TicketsScreen } from "./screens/TicketsScreen";
import { initialState, reduce } from "./state/reducer";
import type { KioskEvent, KioskState } from "./state/types";

type AppConfig = {
  backendBaseUrl: string;
  terminalToken: string;
  hasToken: boolean;
  bridgeHost?: string;
  bridgePort?: number;
};

function readConfig(): AppConfig {
  const env = import.meta.env as ViteEnvLike;
  const resolved = resolveRuntimeConfig(
    env,
    typeof window !== "undefined" ? window.AndroidBridge : undefined
  );
  return {
    backendBaseUrl: resolved.backendBaseUrl,
    terminalToken: resolved.terminalToken,
    hasToken: resolved.hasToken,
    bridgeHost: import.meta.env.VITE_BRIDGE_HOST as string | undefined,
    bridgePort: import.meta.env.VITE_BRIDGE_PORT
      ? Number(import.meta.env.VITE_BRIDGE_PORT)
      : undefined,
  };
}

// Shown when neither the native bridge nor the env provided a terminal
// token. Phrased for the technician who opens the totem, not the visitor:
// the fault is provisioning, not the network. Matches the hardcoded-Spanish
// convention the reducer uses for its other out-of-service reasons.
const UNPROVISIONED_REASON =
  "Terminal sin aprovisionar: falta la credencial del tótem.";

export function App() {
  const config = useMemo(readConfig, []);
  const api = useMemo(
    () =>
      new ApiClient({
        backendBaseUrl: config.backendBaseUrl,
        terminalToken: config.terminalToken,
      }),
    [config.backendBaseUrl, config.terminalToken]
  );
  const eventLog = useMemo(() => new EventLog(api), [api]);
  // IM30 status codes seen during the current sale; reset when a sale starts
  // and attached to its sale.outcome event.
  const statusesRef = useRef<{ code: string; atMs: number }[]>([]);
  // Raw IM30 frames the bridge delivered beside the current sale's outcome;
  // reset when a sale starts and attached to its sale.outcome event.
  const framesRef = useRef<SaleFrames | null>(null);
  // Order of the sale in progress, so transport diagnostics (which carry no
  // order id) can still be tagged with it. Null outside a sale.
  const currentOrderIdRef = useRef<number | null>(null);
  const bridge = useMemo(
    () =>
      new BridgeClient({
        host: config.bridgeHost,
        port: config.bridgePort,
        // The bridge emits diagnostics before the sale promise settles, so
        // framesRef is populated by the time runSaleOnBridge records.
        onDiagnostic: (d) => {
          if (d.kind === "sale.parse_error") {
            eventLog.record(
              "sale.parse_error",
              { rawEvent: d.rawEvent, issues: d.issues },
              currentOrderIdRef.current ?? undefined
            );
          } else {
            framesRef.current = { frames: d.frames, requestId: d.requestId };
          }
        },
      }),
    [config.bridgeHost, config.bridgePort, eventLog]
  );

  // No token means every backend call would 401. Stop here rather than
  // booting to the attract screen and failing three taps later with a
  // network-flavoured error that sends the technician after the wrong fault.
  // Both providers still wrap it — OutOfServiceScreen resolves copy through
  // useLang() and renders BrandMark, which reads useKioskConfig() — but
  // api={null} keeps the config provider on its bundled defaults so no
  // doomed request goes out, and KioskApp (bridge subscription, state
  // machine) never mounts.
  if (!config.hasToken) {
    return (
      <KioskConfigProvider api={null}>
        <LangProvider>
          <OutOfServiceScreen
            state={{ phase: "out-of-service", reason: UNPROVISIONED_REASON }}
          />
        </LangProvider>
      </KioskConfigProvider>
    );
  }

  return (
    <KioskConfigProvider api={api}>
      <LangProvider>
        <KioskApp
          api={api}
          bridge={bridge}
          currentOrderIdRef={currentOrderIdRef}
          eventLog={eventLog}
          framesRef={framesRef}
          statusesRef={statusesRef}
        />
      </LangProvider>
    </KioskConfigProvider>
  );
}

function KioskApp({
  api,
  bridge,
  eventLog,
  statusesRef,
  framesRef,
  currentOrderIdRef,
}: {
  api: ApiClient;
  bridge: BridgeClient;
  eventLog: EventLog;
  statusesRef: MutableRefObject<{ code: string; atMs: number }[]>;
  framesRef: MutableRefObject<SaleFrames | null>;
  currentOrderIdRef: MutableRefObject<number | null>;
}) {
  const { doneResetMs, museumName } = useKioskConfig();
  const { setLang } = useLang();

  const [state, dispatch] = useReducer(reduce, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  // Subscribe to bridge /events on mount
  useEffect(() => {
    const unsubscribe = bridge.subscribe({
      onEvent: (e) => {
        if (e.type === "im30.status") {
          statusesRef.current.push({ code: e.code, atMs: Date.now() });
          dispatch({
            type: "sale-status",
            code: e.code,
            message: e.message,
          });
        }
      },
      onClose: () => {
        if (stateRef.current.phase !== "out-of-service") {
          dispatch({
            type: "bridge-disconnected",
            detail: "WebSocket cerrado",
          });
        }
      },
    });
    return unsubscribe;
  }, [bridge, statusesRef]);

  // Reset to the default language whenever the flow returns to idle, so the
  // next visitor always starts in Spanish.
  useEffect(() => {
    if (state.phase === "idle") {
      setLang("es");
    }
  }, [state.phase, setLang]);

  // Phase-driven side effects. The effect re-runs ONLY when state.phase
  // transitions — the rest of state is read through stateRef so the closure
  // sees the latest values without forcing the effect to re-fire on every
  // intra-phase mutation (e.g., set-quantity inside select-tickets).
  // biome-ignore lint/correctness/useExhaustiveDependencies: stateRef intentional
  useEffect(() => {
    const current = stateRef.current;
    if (current.phase === "loading-ticket-types") {
      runLoadTicketTypes(api, current.date, dispatch);
    }
    if (current.phase === "creating-order") {
      runCreateOrder(
        api,
        {
          visitDate: current.date,
          items: Object.entries(current.quantities)
            .filter(([, q]) => q > 0)
            .map(([id, q]) => ({ ticketTypeId: Number(id), quantity: q })),
        },
        dispatch
      );
    }
    const saleDeps = {
      bridge,
      api,
      eventLog,
      statuses: () => statusesRef.current,
      frames: () => framesRef.current,
    };
    if (current.phase === "awaiting-card") {
      const orderId = current.orderId;
      statusesRef.current = [];
      framesRef.current = null;
      currentOrderIdRef.current = orderId;
      void runSaleOnBridge(
        saleDeps,
        {
          orderId,
          amountCLP: current.amountCLP,
          ticketNumber: current.ticketNumber,
        },
        dispatch
      ).finally(() => {
        if (currentOrderIdRef.current === orderId) {
          currentOrderIdRef.current = null;
        }
      });
    }
    if (current.phase === "confirming") {
      void runConfirmOrder(saleDeps, current.orderId, current.im30, dispatch);
    }
    if (current.phase === "printing") {
      runPrint(
        bridge,
        current.orderId,
        current.tickets,
        current.voucher,
        current.last4,
        museumName,
        dispatch
      );
    }
    if (current.phase === "done") {
      const t = setTimeout(
        () => dispatch({ type: "idle-timeout" }),
        doneResetMs
      );
      return () => clearTimeout(t);
    }
    return;
  }, [state.phase, api, bridge, eventLog, doneResetMs, museumName]);

  return <Routed dispatch={dispatch} state={state} />;
}

function Routed({
  state,
  dispatch,
}: {
  state: KioskState;
  dispatch: (e: KioskEvent) => void;
}) {
  switch (state.phase) {
    case "idle":
      return <AttractScreen dispatch={dispatch} />;
    case "select-date":
      return <DateScreen dispatch={dispatch} />;
    case "loading-ticket-types":
      return <LoadingScreen kind="tickets" />;
    case "select-tickets":
      return <TicketsScreen dispatch={dispatch} state={state} />;
    case "review":
      return <ReviewScreen dispatch={dispatch} state={state} />;
    case "creating-order":
      return <LoadingScreen kind="order" />;
    case "awaiting-card":
      return <AwaitCardScreen state={state} />;
    case "card-processing":
      return <CardProcessingScreen state={state} />;
    case "confirming":
      return <LoadingScreen kind="confirm" />;
    case "printing":
      return <PrintingScreen state={state} />;
    case "done":
      return <DoneScreen dispatch={dispatch} state={state} />;
    case "payment-failed":
      return <PaymentFailedScreen dispatch={dispatch} state={state} />;
    case "print-error":
      return <PrintErrorScreen dispatch={dispatch} state={state} />;
    case "out-of-service":
      return <OutOfServiceScreen state={state} />;
    default:
      return (
        <OutOfServiceScreen
          state={{ phase: "out-of-service", reason: "Estado desconocido" }}
        />
      );
  }
}

function runLoadTicketTypes(
  api: ApiClient,
  date: string,
  dispatch: (e: KioskEvent) => void
): void {
  api
    .ticketTypes(date)
    .then((res) =>
      dispatch({
        type: "ticket-types-loaded",
        ticketTypes: res.ticketTypes,
      })
    )
    .catch((e: unknown) => {
      const message = e instanceof Error ? e.message : String(e);
      dispatch({
        type: "bridge-disconnected",
        detail: `No se pudo cargar tipos de entrada: ${message}`,
      });
    });
}

function runCreateOrder(
  api: ApiClient,
  body: CreateOrderRequest,
  dispatch: (e: KioskEvent) => void
): void {
  api
    .createOrder(body)
    .then((res) =>
      dispatch({
        type: "order-created",
        orderId: res.orderId,
        amountCLP: res.amountCLP,
        ticketNumber: res.ticketNumberForIm30,
      })
    )
    .catch((e: unknown) => {
      const message = e instanceof Error ? e.message : String(e);
      dispatch({ type: "order-failed", reason: message });
    });
}

async function runPrint(
  bridge: BridgeClient,
  orderId: number,
  tickets: PrintableTicket[],
  voucher: string | null,
  last4: string | null,
  museumName: string,
  dispatch: (e: KioskEvent) => void
): Promise<void> {
  try {
    if (voucher) {
      const job: PrintJob = { kind: "voucher", text: voucher, cutAtEnd: true };
      const res = await bridge.print(job);
      if (!res.ok) {
        dispatch({
          type: "print-failed",
          error: res.error ?? "Falló impresión del comprobante",
        });
        return;
      }
    }
    for (let i = 0; i < tickets.length; i += 1) {
      const t = tickets[i];
      if (!t) {
        continue;
      }
      const job: PrintJob = {
        kind: "ticket",
        museumName,
        ticketTypeName: t.ticketTypeName,
        visitDate: t.visitDate,
        displayTicketNumber: t.displayTicketNumber,
        uniqueCode: t.uniqueCode,
        priceCLP: t.priceCLP,
        orderId,
        last4,
      };
      const res = await bridge.print(job);
      if (!res.ok) {
        dispatch({
          type: "print-failed",
          error:
            res.error ??
            `Falló impresión de la entrada ${t.displayTicketNumber}`,
        });
        return;
      }
      dispatch({ type: "print-progress", printedCount: i + 1 });
    }
    dispatch({ type: "print-completed" });
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    dispatch({ type: "print-failed", error: message });
  }
}
